import { useColorScheme } from "@ugitgud/legos/hooks/useColorScheme";
import { WithClassName, WithColorScheme } from "@ugitgud/legos/ui/shared";
import React, { useMemo } from "react";
import { twMerge } from "tailwind-merge";

const inputBaseStyles = "rounded-sm text-xs focus:outline-none focus-within:outline-none";

const colorSchemeClasses = {
  secondary: "dark:bg-purple-100 placeholder:text-lavender-200 text-lavender-50",
};

export type InputColorSchemes = keyof typeof colorSchemeClasses;

const tagSpecificStyles = {
  textarea: "p-[0.666em] outline-none resize-none",
};

export type AcceptedInputTypes =
  | "number"
  | "time"
  | "text"
  | "image"
  | "color"
  | "date"
  | "datetime-local"
  | "file"
  | "email"
  | "hidden"
  | "month"
  | "password"
  | "range"
  | "search"
  | "tel"
  | "url"
  | "week";

const textFieldStyles = "px-3 py-2.5";
const inputTypeStyles: {
  [key in AcceptedInputTypes]?: string;
} = {
  text: textFieldStyles,
  email: textFieldStyles,
  password: textFieldStyles,
  search: textFieldStyles,
  tel: textFieldStyles,
  url: textFieldStyles,
};

export type InputProps = React.ComponentPropsWithoutRef<"input"> &
  WithClassName<{
    type?: AcceptedInputTypes;
  }> &
  WithColorScheme<{}, InputColorSchemes>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { className, type = "text", colorScheme, ...elementProps } = props;
  const colorSchemeDef = useColorScheme({
    classes: colorSchemeClasses,
    scheme: colorScheme,
    default: "secondary",
  });

  const finalClasses = useMemo(() => {
    const typeClasses = inputTypeStyles[type || "text"];
    return twMerge(inputBaseStyles, colorSchemeDef, typeClasses, className);
  }, [colorSchemeDef, className]);

  return <input type={type} className={finalClasses} {...elementProps} ref={ref} />;
});

export type TextAreaProps = WithClassName<React.ComponentPropsWithoutRef<"textarea">> & WithColorScheme<{}, InputColorSchemes>;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(({ className, colorScheme, ...rest }, ref) => {
  const colorSchemeDef = useColorScheme({
    classes: colorSchemeClasses,
    scheme: colorScheme,
    default: "secondary",
  });
  const finalClasses = useMemo(() => {
    return twMerge(inputBaseStyles, tagSpecificStyles["textarea"], colorSchemeDef, className);
  }, [className]);
  return <textarea className={finalClasses} {...rest} ref={ref} />;
});
